import { z } from "zod";

/** A completed checkout reconstructed from retained evidence, never an action input. */
export const reconstructedCheckoutRowSchema = z.object({
  id: z.string().min(1),
  appointmentId: z.string().min(1),
  amountMinor: z.number().int().nonnegative(),
  currency: z.string().regex(/^[a-z]{3}$/),
  paymentType: z.enum(["CASH", "CARD"]),
  serviceAmountMinor: z.number().int().nonnegative(),
  tipAmountMinor: z.number().int().nonnegative(),
  recordedAt: z.string(),
  recordedAtSource: z.string(),
  source: z.literal("RECONSTRUCTED"),
  sourceVerified: z.literal(false),
  readOnly: z.literal(true),
  associationSource: z.literal("RECONSTRUCTED_FROM_RETAINED_EVIDENCE"),
  allocationSource: z.enum(["PROCESSOR_EVIDENCE", "OWNER_CONFIRMATION"]),
  reconstructedAt: z.string(),
}).refine(
  (row) => row.serviceAmountMinor + row.tipAmountMinor === row.amountMinor,
  "Reconstructed service and tip must equal the recorded payment",
);

export type ReconstructedCheckoutRow = z.infer<typeof reconstructedCheckoutRowSchema>;

/** Explicit public whitelist: confirmation text, raw evidence and hashes stay private. */
export function toReconstructedCheckoutRow(record: {
  checkoutId: string; appointmentId: string; amountMinor: number; currency: string;
  paymentType: string; serviceAmountMinor: number; tipAmountMinor: number;
  associationSource: string; allocationSource: string; createdAt: Date;
  ledgerHistory: { recordedAt: Date; recordedAtSource: string };
}): ReconstructedCheckoutRow {
  return reconstructedCheckoutRowSchema.parse({
    id: record.checkoutId,
    appointmentId: record.appointmentId,
    amountMinor: record.amountMinor,
    currency: record.currency,
    paymentType: record.paymentType,
    serviceAmountMinor: record.serviceAmountMinor,
    tipAmountMinor: record.tipAmountMinor,
    recordedAt: record.ledgerHistory.recordedAt.toISOString(),
    recordedAtSource: record.ledgerHistory.recordedAtSource,
    source: "RECONSTRUCTED",
    sourceVerified: false,
    readOnly: true,
    associationSource: record.associationSource,
    allocationSource: record.allocationSource,
    reconstructedAt: record.createdAt.toISOString(),
  });
}
